/**
 * Frontend permissions utility - mirrors backend constants/permissions.js
 * Values MUST match the backend exactly.
 */

// All permission constants (must match backend)
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',

  // Home Setting
  HOME_SETTING_VIEW: 'home_setting.view',
  HOME_SETTING_EDIT: 'home_setting.edit',

  // Manage Users (Admin Users)
  USERS_VIEW: 'admin_users.view',
  USERS_CREATE: 'admin_users.create',
  USERS_EDIT: 'admin_users.edit',
  USERS_DELETE: 'admin_users.delete',

  // Students
  STUDENTS_VIEW: 'students.view',
  STUDENTS_CREATE: 'students.create',
  STUDENTS_EDIT: 'students.edit',
  STUDENTS_DELETE: 'students.delete',

  // Schools (School Portal)
  SCHOOLS_VIEW: 'schools.view',
  SCHOOLS_EDIT: 'schools.edit',
  SCHOOLS_DELETE: 'schools.delete',
  SCHOOLS_PAYMENTS_VIEW: 'schools.payments.view',
  SCHOOLS_PAYMENTS_REVIEW: 'schools.payments.review',

  // Banners
  BANNERS_VIEW: 'banners.view',
  BANNERS_CREATE: 'banners.create',
  BANNERS_EDIT: 'banners.edit',
  BANNERS_DELETE: 'banners.delete',

  // CMS Pages
  CMS_VIEW: 'cms_pages.view',
  CMS_CREATE: 'cms_pages.create',
  CMS_EDIT: 'cms_pages.edit',
  CMS_DELETE: 'cms_pages.delete',

  // Email Templates
  EMAIL_TEMPLATES_VIEW: 'email_templates.view',
  EMAIL_TEMPLATES_CREATE: 'email_templates.create',
  EMAIL_TEMPLATES_EDIT: 'email_templates.edit',
  EMAIL_TEMPLATES_DELETE: 'email_templates.delete',

  // Categories
  CATEGORIES_VIEW: 'categories.view',
  CATEGORIES_CREATE: 'categories.create',
  CATEGORIES_EDIT: 'categories.edit',
  CATEGORIES_DELETE: 'categories.delete',

  // Subscriptions
  SUBSCRIPTIONS_VIEW: 'subscriptions.view',
  SUBSCRIPTIONS_CREATE: 'subscriptions.create',
  SUBSCRIPTIONS_EDIT: 'subscriptions.edit',
  SUBSCRIPTIONS_DELETE: 'subscriptions.delete',

  // Discounts
  DISCOUNTS_VIEW: 'discounts.view',
  DISCOUNTS_CREATE: 'discounts.create',
  DISCOUNTS_EDIT: 'discounts.edit',
  DISCOUNTS_DELETE: 'discounts.delete',

  // Currency Settings
  CURRENCY_SETTINGS_VIEW: 'currency_settings.view',
  CURRENCY_SETTINGS_EDIT: 'currency_settings.edit',

  // Student Subscriptions
  STUDENT_SUBSCRIPTIONS_VIEW: 'student_subscriptions.view',

  // Registered Subjects
  REGISTERED_SUBJECTS_VIEW: 'registered_subjects.view',

  // Exams
  EXAMS_VIEW: 'exams.view',
  EXAMS_CREATE: 'exams.create',
  EXAMS_EDIT: 'exams.edit',
  EXAMS_DELETE: 'exams.delete',

  // University
  UNIVERSITY_VIEW: 'university.view',
  UNIVERSITY_CREATE: 'university.create',
  UNIVERSITY_EDIT: 'university.edit',
  UNIVERSITY_DELETE: 'university.delete',

  // Assessments
  ASSESSMENTS_VIEW: 'assessments.view',
  ASSESSMENTS_CREATE: 'assessments.create',
  ASSESSMENTS_EDIT: 'assessments.edit',
  ASSESSMENTS_DELETE: 'assessments.delete',

  // Courses
  COURSES_VIEW: 'courses.view',
  COURSES_CREATE: 'courses.create',
  COURSES_EDIT: 'courses.edit',
  COURSES_DELETE: 'courses.delete',

  // Subjects
  SUBJECTS_VIEW: 'subjects.view',
  SUBJECTS_CREATE: 'subjects.create',
  SUBJECTS_EDIT: 'subjects.edit',
  SUBJECTS_DELETE: 'subjects.delete',

  // Questions
  QUESTIONS_VIEW: 'questions.view',
  QUESTIONS_CREATE: 'questions.create',
  QUESTIONS_EDIT: 'questions.edit',
  QUESTIONS_DELETE: 'questions.delete',

  // Topics
  TOPICS_VIEW: 'topics.view',
  TOPICS_CREATE: 'topics.create',
  TOPICS_EDIT: 'topics.edit',
  TOPICS_DELETE: 'topics.delete',

  // Theories
  THEORIES_VIEW: 'theories.view',
  THEORIES_CREATE: 'theories.create',
  THEORIES_EDIT: 'theories.edit',
  THEORIES_DELETE: 'theories.delete',

  // Advices
  ADVICES_VIEW: 'advices.view',
  ADVICES_CREATE: 'advices.create',
  ADVICES_EDIT: 'advices.edit',
  ADVICES_DELETE: 'advices.delete',

  // News
  NEWS_VIEW: 'news.view',
  NEWS_CREATE: 'news.create',
  NEWS_EDIT: 'news.edit',
  NEWS_DELETE: 'news.delete',

  // Contact Us
  CONTACTUS_VIEW: 'contact_us.view',
  CONTACTUS_REPLY: 'contact_us.reply',
  CONTACTUS_DELETE: 'contact_us.delete',

  // Image Notifications
  IMAGE_NOTIFICATION_VIEW: 'image_notifications.view',
  IMAGE_NOTIFICATION_CREATE: 'image_notifications.create',
  IMAGE_NOTIFICATION_EDIT: 'image_notifications.edit',
  IMAGE_NOTIFICATION_DELETE: 'image_notifications.delete',

  // Newsletter
  NEWSLETTER_VIEW: 'newsletter.view',
  NEWSLETTER_DELETE: 'newsletter.delete',

  SURVEY_VIEW: 'survey.view',
  SURVEY_DELETE: 'survey.delete',

  // Testimonials
  TESTIMONIALS_VIEW: 'testimonials.view',
  TESTIMONIALS_CREATE: 'testimonials.create',
  TESTIMONIALS_EDIT: 'testimonials.edit',
  TESTIMONIALS_DELETE: 'testimonials.delete',

  // FAQs
  FAQS_VIEW: 'faqs.view',
  FAQS_CREATE: 'faqs.create',
  FAQS_EDIT: 'faqs.edit',
  FAQS_DELETE: 'faqs.delete',

  // Roles & Permissions
  ROLES_VIEW: 'roles.view',
  ROLES_CREATE: 'roles.create',
  ROLES_EDIT: 'roles.edit',
  ROLES_DELETE: 'roles.delete',

  // Reports
  REPORTS_VIEW: 'admin_reports.view',
  REPORTS_EXPORT: 'admin_reports.export',
};

/**
 * Get the permissions array for the current user from the auth state user object.
 * The user object from /me will have roleId populated with the full role document.
 * SuperAdmins get all permissions.
 */
export const getUserPermissions = (user) => {
  if (!user) return [];

  // SuperAdmins have all permissions
  if (user.isSuperAdmin) {
    return Object.values(PERMISSIONS);
  }

  // Get permissions from populated role
  if (user.roleId && user.roleId.permissions) {
    return user.roleId.permissions;
  }

  // Legacy admin users without a role assignment get all permissions (backward compat)
  if (user.role === 'admin' && !user.roleId) {
    return Object.values(PERMISSIONS);
  }

  return [];
};

/**
 * Check if a user has a specific permission.
 * @param {Object} user - The user object from Redux auth state
 * @param {string|string[]} requiredPermissions - Permission(s) to check
 * @param {string} mode - 'any' (default) or 'all'
 * @returns {boolean}
 */
export const hasPermission = (user, requiredPermissions, mode = 'any') => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  const userPermissions = getUserPermissions(user);
  const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  if (mode === 'all') {
    return required.every((p) => userPermissions.includes(p));
  }
  return required.some((p) => userPermissions.includes(p));
};

/**
 * Group permissions by module for display in role management UI.
 */
export const getPermissionGroups = () => {
  const groups = {};
  Object.values(PERMISSIONS).forEach((perm) => {
    const [module] = perm.split('.');
    if (!groups[module]) {
      groups[module] = [];
    }
    groups[module].push(perm);
  });
  return groups;
};
